import { useEffect, useReducer, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  ImagePlus,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { confirmAction } from '@/lib/confirm';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { quotePageSelectionKey } from '@/editor/extensions/quotePageSelection';
import api from '@/lib/api';
import {
  resolveTeamMember,
  resolveTeamMemberId,
  useTeamMembers,
  useTeamMemberStore,
} from '@/store/teamMemberStore';
import type { TeamBlockContent, TeamCatalogMember } from '@/types';

export const MAX_TEAM_MEMBERS = 8;

function SelectedMember({
  id,
  index,
  count,
  content,
  catalog,
  loading,
  onRole,
  onMove,
  onRemove,
}: {
  id: string;
  index: number;
  count: number;
  content: TeamBlockContent;
  catalog: Record<string, TeamCatalogMember>;
  loading: boolean;
  onRole: (role: string) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const member = resolveTeamMember(id, content.members, catalog);
  const roleOverrides = content.roleOverrides || {};
  const name = member?.name ?? id;
  return (
    <li
      ref={setNodeRef}
      className="workspace-overview-item workspace-team-member"
      data-dragging={isDragging || undefined}
      style={{ transform: CSS.Transform.toString(transform && { ...transform, x: 0 }), transition }}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        className="workspace-icon-button workspace-overview-drag"
        {...attributes}
        {...listeners}
        aria-label={`${name} verslepen om te sorteren`}
        title="Versleep om te sorteren"
      >
        <GripVertical size={16} aria-hidden="true" />
      </button>
      {member ? (
        <img className="workspace-team-photo" src={member.img} alt="" draggable={false} />
      ) : (
        <span className="workspace-team-photo" aria-hidden="true" />
      )}
      <div className="workspace-team-member-fields">
        <strong>{name}</strong>
        {member ? (
          <>
            <input
              className="workspace-input"
              value={roleOverrides[id] || roleOverrides[resolveTeamMemberId(id)] || ''}
              onChange={(event) => onRole(event.target.value)}
              placeholder={member.title}
              aria-label={`Functie van ${member.name}`}
            />
            {member.archived && <span>Verwijderd uit de teamlijst, maar staat nog op deze pagina</span>}
          </>
        ) : loading ? (
          <span>Teamlijst laden…</span>
        ) : (
          <span>Staat niet meer in de teamlijst en is verborgen op de pagina</span>
        )}
      </div>
      <div className="workspace-overview-move">
        <button
          type="button"
          className="workspace-icon-button"
          disabled={index === 0}
          aria-label={`${name} omhoog`}
          title="Omhoog"
          onClick={() => onMove(-1)}
        >
          <ArrowUp size={15} />
        </button>
        <button
          type="button"
          className="workspace-icon-button"
          disabled={index === count - 1}
          aria-label={`${name} omlaag`}
          title="Omlaag"
          onClick={() => onMove(1)}
        >
          <ArrowDown size={15} />
        </button>
      </div>
      <button
        type="button"
        className="workspace-icon-button"
        aria-label={`${name} van de teampagina halen`}
        title="Van de teampagina halen"
        onClick={onRemove}
      >
        <X size={15} />
      </button>
    </li>
  );
}

/** Nieuw of bestaand lid van de teamlijst waaruit teampagina's kiezen; zet het niet zelf op een pagina. */
function TeamMemberForm({
  member,
  onDone,
}: {
  member?: TeamCatalogMember;
  onDone: (member?: TeamCatalogMember) => void;
}) {
  const add = useTeamMemberStore((state) => state.add);
  const update = useTeamMemberStore((state) => state.update);
  const fileInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(member?.name ?? '');
  const [title, setTitle] = useState(member?.title ?? '');
  const [photoUrl, setPhotoUrl] = useState(member?.photoUrl ?? '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const complete = Boolean(name.trim() && title.trim() && photoUrl);

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.uploadImage(file);
      setPhotoUrl(api.resolveUploadUrl(result.url));
    } catch (error) {
      toast.error('Foto uploaden mislukt', { error });
    } finally {
      setUploading(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!complete || saving) return;
    setSaving(true);
    const input = { name: name.trim(), title: title.trim(), photoUrl };
    try {
      const saved = member ? await update(member.id, input) : await add(input);
      toast.success(member ? `${saved.name} is bijgewerkt` : `${saved.name} staat nu in de teamlijst`);
      onDone(saved);
    } catch (error) {
      toast.error(member ? 'Teamlid opslaan mislukt' : 'Teamlid toevoegen mislukt', { error });
      setSaving(false);
    }
  };

  return (
    <form
      className="workspace-team-new"
      onSubmit={submit}
      aria-label={member ? `${member.name} bewerken` : 'Nieuw teamlid'}
    >
      <button
        type="button"
        className="workspace-team-new-photo"
        disabled={uploading}
        onClick={() => fileInput.current?.click()}
        aria-label={photoUrl ? 'Andere foto kiezen' : 'Foto kiezen'}
        title={photoUrl ? 'Andere foto kiezen' : 'Foto kiezen'}
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" draggable={false} />
        ) : (
          <ImagePlus size={18} aria-hidden="true" />
        )}
        <span>{uploading ? 'Uploaden…' : photoUrl ? 'Wijzigen' : 'Foto'}</span>
      </button>
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={upload}
      />
      <div className="workspace-team-new-fields">
        <input
          className="workspace-input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Voor- en achternaam"
          aria-label="Naam"
          maxLength={120}
          autoFocus
        />
        <input
          className="workspace-input"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Functie"
          aria-label="Functie"
          maxLength={120}
        />
        <p className="workspace-overview-hint">
          Een staande foto (verhouding 2:3) vult de kaart op de teampagina het best.
        </p>
        {member && (
          <p className="workspace-overview-hint">
            Wijzigingen staan meteen op alle teampagina's, ook in verstuurde offertes.
          </p>
        )}
        <div className="workspace-team-new-actions">
          <Button type="button" variant="ghost" size="sm" onClick={() => onDone()}>
            Annuleren
          </Button>
          <Button type="submit" size="sm" disabled={!complete || saving || uploading}>
            {member
              ? saving
                ? 'Opslaan…'
                : 'Opslaan'
              : saving
                ? 'Toevoegen…'
                : 'Toevoegen aan teamlijst'}
          </Button>
        </div>
      </div>
    </form>
  );
}

/** Titel, volgorde, functies en samenstelling van een teampagina. */
export function TeamMembersEditor({
  content,
  onChange,
}: {
  content: TeamBlockContent;
  onChange: (content: TeamBlockContent) => void;
}) {
  const [search, setSearch] = useState('');
  const [dragging, setDragging] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TeamCatalogMember | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const { members, byId: catalog, status, load, setArchived } = useTeamMembers();
  const candidateList = useRef<HTMLUListElement>(null);
  useEffect(() => {
    // Een net toegevoegd of bewerkt lid: breng het in beeld, klaar om te kiezen.
    candidateList.current?.querySelector<HTMLElement>('[data-added] .workspace-team-candidate')?.focus();
  }, [savedId]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const selected = content.selectedMembers || [];
  const full = selected.length >= MAX_TEAM_MEMBERS;
  const setMembers = (selectedMembers: string[]) => onChange({ ...content, selectedMembers });
  const query = search.trim().toLowerCase();
  const candidates = members.filter(
    (member) =>
      !member.archived &&
      !selected.some((selectedId) => resolveTeamMemberId(selectedId) === member.id) &&
      (!query ||
        member.name.toLowerCase().includes(query) ||
        member.title.toLowerCase().includes(query))
  );

  /** Uit de teamlijst, niet uit offertes: pagina's waar het lid al op staat, tonen het nog. */
  const removeFromList = async (member: TeamCatalogMember) => {
    const firstName = member.name.split(/\s+/)[0];
    if (
      !(await confirmAction({
        title: `${member.name} uit de teamlijst verwijderen?`,
        description: `Je kunt ${firstName} daarna niet meer op een teampagina zetten. Offertes en templates waar ${firstName} al op staat, blijven ongewijzigd.`,
        confirmLabel: 'Verwijderen',
        destructive: true,
      }))
    )
      return;
    try {
      await setArchived(member.id, true);
      toast.success(`${member.name} is uit de teamlijst verwijderd`, {
        action: {
          label: 'Ongedaan maken',
          onClick: () => {
            setArchived(member.id, false).catch((error) =>
              toast.error('Ongedaan maken mislukt', { error })
            );
          },
        },
      });
    } catch (error) {
      toast.error(`${member.name} verwijderen mislukt`, { error });
    }
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragging(false);
    if (!over || active.id === over.id) return;
    const from = selected.indexOf(String(active.id));
    const to = selected.indexOf(String(over.id));
    if (from >= 0 && to >= 0) setMembers(arrayMove(selected, from, to));
  };

  return (
    <div className="workspace-team-editor">
      <label className="workspace-team-field">
        Paginatitel in offerte
        <input
          className="workspace-input"
          value={content.title ?? ''}
          onChange={(event) => onChange({ ...content, title: event.target.value })}
          placeholder="Het team"
        />
      </label>

      <section aria-labelledby="workspace-team-members-title">
        <header className="workspace-overviews-heading">
          <h4 id="workspace-team-members-title">
            Teamleden{' '}
            <span>
              {selected.length} van {MAX_TEAM_MEMBERS}
            </span>
          </h4>
        </header>
        {selected.length > 1 && (
          <p className="workspace-overview-hint">
            Versleep de teamleden of gebruik de pijltjes om de volgorde op de pagina te bepalen.
          </p>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={() => setDragging(true)}
          onDragCancel={() => setDragging(false)}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={selected} strategy={verticalListSortingStrategy}>
            <ol className="workspace-overviews-list" data-overview-sorting={dragging || undefined}>
              {selected.map((id, index) => (
                <SelectedMember
                  key={id}
                  id={id}
                  index={index}
                  count={selected.length}
                  content={content}
                  catalog={catalog}
                  loading={status !== 'ready'}
                  onRole={(role) =>
                    onChange({ ...content, roleOverrides: { ...content.roleOverrides, [id]: role } })
                  }
                  onMove={(direction) => setMembers(arrayMove(selected, index, index + direction))}
                  onRemove={() => setMembers(selected.filter((selectedId) => selectedId !== id))}
                />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
        {!selected.length && (
          <p className="workspace-overview-hint">
            Er staan nog geen teamleden op deze pagina. Kies hieronder wie je wilt tonen.
          </p>
        )}
      </section>

      <section aria-labelledby="workspace-team-add-title">
        <header className="workspace-overviews-heading">
          <h4 id="workspace-team-add-title">Teamlid toevoegen</h4>
          {!creating && (
            <button
              type="button"
              className="workspace-quiet-button"
              onClick={() => {
                setEditing(null);
                setCreating(true);
              }}
              title="Een collega aan de teamlijst toevoegen, zodat je die op teampagina's kunt zetten"
            >
              <UserPlus size={14} aria-hidden="true" />
              Nieuw in de teamlijst
            </button>
          )}
        </header>
        {creating && (
          <TeamMemberForm
            onDone={(member) => {
              setCreating(false);
              if (!member) return;
              setSearch('');
              setSavedId(member.id);
            }}
          />
        )}
        {editing && (
          <TeamMemberForm
            key={editing.id}
            member={editing}
            onDone={(member) => {
              setEditing(null);
              if (member) setSavedId(member.id);
            }}
          />
        )}
        <label className="workspace-team-search">
          <Search size={14} aria-hidden="true" />
          <input
            className="workspace-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Zoek op naam of functie"
            aria-label="Zoek teamleden"
          />
        </label>
        {full && (
          <p className="workspace-overview-hint">
            Er passen maximaal {MAX_TEAM_MEMBERS} teamleden op één teampagina. Haal eerst iemand
            van de pagina om een ander toe te voegen.
          </p>
        )}
        <ul ref={candidateList} className="workspace-team-candidates">
          {candidates.map((member) => (
            <li key={member.id} data-added={member.id === savedId || undefined}>
              <button
                type="button"
                className="workspace-team-candidate"
                disabled={full}
                onClick={() => setMembers([...selected, member.id])}
                aria-label={`${member.name} toevoegen`}
              >
                <img className="workspace-team-photo" src={member.photoUrl} alt="" draggable={false} />
                <span>
                  <strong>{member.name}</strong>
                  <span>{member.title}</span>
                </span>
                <Plus size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="workspace-icon-button workspace-team-candidate-edit"
                onClick={() => {
                  setCreating(false);
                  setEditing(member);
                }}
                aria-label={`${member.name} bewerken`}
                title="Naam, functie of foto bewerken"
              >
                <Pencil size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="workspace-icon-button workspace-team-candidate-remove"
                onClick={() => void removeFromList(member)}
                aria-label={`${member.name} uit de teamlijst verwijderen`}
                title="Uit de teamlijst verwijderen"
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
        {status === 'loading' && <p className="workspace-overview-hint">Teamlijst laden…</p>}
        {status === 'error' && (
          <p className="workspace-overview-hint">
            De teamlijst kon niet worden geladen.{' '}
            <button type="button" className="workspace-link" onClick={() => void load()}>
              Opnieuw proberen
            </button>
          </p>
        )}
        {status === 'ready' && !candidates.length && (
          <p className="workspace-overview-hint">
            {query ? `Geen teamleden gevonden voor “${search.trim()}”.` : 'Het hele team staat al op deze pagina.'}
          </p>
        )}
      </section>
    </div>
  );
}

function findPage(doc: PMNode, blockId: string) {
  const pages: Array<{ node: PMNode; pos: number }> = [];
  doc.forEach((node, pos) => pages.push({ node, pos }));
  return pages.find(({ node }) => node.attrs.id === blockId);
}

/** Teampagina in het bewerkpaneel; wijzigingen gaan direct het document in. */
export default function TeamPageEditor({ editor, blockId }: { editor: Editor; blockId: string }) {
  const [, refresh] = useReducer((value) => value + 1, 0);
  useEffect(() => {
    editor.on('update', refresh);
    return () => {
      editor.off('update', refresh);
    };
  }, [editor]);
  const page = findPage(editor.state.doc, blockId);
  if (!page) return null;

  const update = (content: TeamBlockContent) => {
    const live = findPage(editor.state.doc, blockId);
    if (!live) return;
    const { selection } = editor.state;
    const tr = editor.state.tr.setNodeMarkup(live.pos, undefined, { ...live.node.attrs, content });
    // Een teampagina is een atom: zonder herstel valt de selectie weg en sluit dit paneel.
    if (selection instanceof NodeSelection && selection.from === live.pos)
      tr.setSelection(NodeSelection.create(tr.doc, live.pos));
    editor.view.dispatch(tr.setMeta(quotePageSelectionKey, blockId));
  };

  return (
    <div className="workspace-investment-overview">
      <div className="workspace-overview-title">
        <h3>Teampagina</h3>
      </div>
      <TeamMembersEditor content={(page.node.attrs.content ?? {}) as TeamBlockContent} onChange={update} />
    </div>
  );
}
